import type { CountryCode } from "@gately/validators";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Check } from "lucide-react";

import { GoogleIcon, MicrosoftIcon } from "@gately/ui/brand-icons";
import { Button } from "@gately/ui/button";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import { Logo } from "@gately/ui/logo";
import { PhoneInput } from "@gately/ui/phone-input";
import { toast } from "@gately/ui/toast";
import { parsePhone } from "@gately/validators";

import { authClient } from "~/auth/client";
import { safeRedirectPath } from "~/lib/auth-redirect";
import { orpc } from "~/lib/orpc";

export const Route = createFileRoute("/sign-in")({
  // Better-Auth redirects back here with ?error=… when the OAuth callback
  // fails (e.g. signup disabled for an unknown email). Validated loosely —
  // any string, or absent. `error?:` (not `error: string | undefined`) keeps
  // it an optional search param, so `redirect({ to: "/sign-in" })` elsewhere
  // doesn't need to pass one.
  //
  // `redirect` is the in-app path a signed-out deep link came from (set by
  // the dashboard layout); only a same-origin path survives `safeRedirectPath`.
  validateSearch: (
    search: Record<string, unknown>,
  ): { error?: string; redirect?: string } => ({
    error: typeof search.error === "string" ? search.error : undefined,
    redirect: safeRedirectPath(search.redirect),
  }),
  beforeLoad: async ({ context, search }) => {
    // Already signed in → no reason to be on /sign-in. Bounce to where the
    // deep link pointed, or home. `me.get` is the cheapest auth probe.
    try {
      await context.queryClient.ensureQueryData(orpc.me.get.queryOptions());
      // TanStack Router `redirect()` returns a Response, not an Error —
      // throwing it is the framework's intended navigation signal.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw search.redirect
        ? redirect({ href: search.redirect })
        : redirect({ to: "/" });
    } catch (err) {
      // ORPC's UNAUTHORIZED throws here → signed out, render the page.
      // Re-throw if it's already a redirect (router signal).
      if (
        err &&
        typeof err === "object" &&
        "isRedirect" in err &&
        (err as { isRedirect?: boolean }).isRedirect
      ) {
        throw err;
      }
    }
  },
  component: SignInPage,
});

type Step = "identifier" | "code";

/**
 * Provider sign-in button. The brand mark is absolutely positioned so the
 * label stays optically centred regardless of how wide the mark is — the
 * layout both Google's and Microsoft's branding guidelines ask for.
 */
function SocialButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="relative w-full justify-center font-medium"
      disabled={disabled}
      onClick={onClick}
    >
      <span className="absolute left-4 flex size-5 items-center justify-center">
        {icon}
      </span>
      {label}
    </Button>
  );
}

function SignInPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { error, redirect: returnTo } = Route.useSearch();
  const [step, setStep] = useState<Step>("identifier");
  const [country, setCountry] = useState<CountryCode>("MA");
  const [phone, setPhone] = useState("");
  const e164 = parsePhone(phone, country);
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);

  // Better-Auth's OAuth callback redirects here with ?error=… on failure —
  // most commonly "signup disabled" for a cold sign-in with no
  // pre-provisioned account (Gately is invite-only).
  useEffect(() => {
    if (error) {
      toast.error(
        "Compte introuvable. Demandez à votre entreprise de vous inviter d'abord.",
      );
    }
  }, [error]);

  async function sendOtp(e: React.FormEvent) {
    e.preventDefault();
    setPending(true);
    if (!e164) {
      toast.error("Numéro de téléphone invalide.");
      setPending(false);
      return;
    }
    try {
      const { error } = await authClient.phoneNumber.sendOtp({
        phoneNumber: e164,
      });
      if (error) throw new Error(error.message ?? "Couldn't send the code.");
      setStep("code");
    } catch (err) {
      const raw = err instanceof Error ? err.message : "";
      const notFound = /sign.?up|not found|disabled|introuvable/i.test(raw);
      toast.error(
        notFound
          ? "Aucun compte trouvé pour ce numéro. L'accès à Gately se fait sur invitation."
          : raw !== ""
            ? raw
            : "Couldn't send the code.",
      );
    } finally {
      setPending(false);
    }
  }

  async function verifyOtp(e: React.FormEvent) {
    e.preventDefault();
    setPending(true);
    try {
      const { error } = await authClient.phoneNumber.verify({
        phoneNumber: e164 ?? "",
        code,
      });
      if (error) throw new Error(error.message ?? "Invalid code.");
      // A new identity: nothing cached before this point may be reused. The
      // query cache holds this page's own 401 probe (and, after an expired
      // session, another user's data); the router cache holds matches whose
      // loaders ran as someone else. Both are cleared so `/` is a clean first
      // navigation — see user-menu.tsx for the failure this prevents.
      queryClient.clear();
      router.clearCache();
      await (returnTo ? navigate({ href: returnTo }) : navigate({ to: "/" }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Invalid code.");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="grid min-h-screen lg:grid-cols-2">
      {/* Left brand panel (desktop only) */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-neutral-950 p-12 text-white lg:flex">
        <div
          className="pointer-events-none absolute -top-40 right-[-120px] size-[520px]"
          style={{
            background:
              "radial-gradient(circle, rgba(247,91,30,0.22), rgba(247,91,30,0) 68%)",
          }}
        />
        <div className="relative flex items-center gap-3">
          <Logo variant="light" withWordmark />
          <span className="text-muted-foreground rounded-full border border-white/15 px-2 py-0.5 font-mono text-[11px] tracking-widest">
            ADMIN
          </span>
        </div>
        <div className="relative">
          <div className="text-primary mb-6 font-mono text-xs tracking-[0.14em] uppercase">
            — Console d'administration
          </div>
          <h2 className="max-w-md text-5xl font-bold tracking-tight text-white">
            Gérez chaque accès, sans friction.
          </h2>
          <p className="text-muted-foreground mt-5 max-w-md text-base">
            Invitations QR, occupants, portails et journal en temps réel — pour
            vos résidences, resorts et immeubles de bureaux, depuis une seule
            console.
          </p>
        </div>
        <ul className="relative flex flex-col gap-3 text-sm">
          {[
            "Journal d'accès horodaté et infalsifiable",
            "Multi-sites, un seul tableau de bord",
            "Conforme à la loi 09-08 sur les données",
          ].map((t) => (
            <li key={t} className="flex items-center gap-3">
              <span className="bg-success-tint text-success inline-flex size-6 items-center justify-center rounded-full">
                <Check className="size-3.5" />
              </span>
              <span className="text-neutral-300">{t}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Logo variant="dark" withWordmark className="mb-8 lg:hidden" />
          <h1 className="text-3xl font-bold tracking-tight">Connexion</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Accédez à votre console Gately.
          </p>

          {step === "identifier" ? (
            <>
              <form onSubmit={sendOtp} className="mt-8 flex flex-col gap-2">
                <Label htmlFor="identifier">Numéro de téléphone</Label>
                <PhoneInput
                  id="identifier"
                  country={country}
                  onCountryChange={setCountry}
                  value={phone}
                  onChange={setPhone}
                  required
                />
                <Button
                  type="submit"
                  className="mt-3"
                  disabled={pending || !e164}
                >
                  {pending ? "Envoi…" : "Envoyer le code →"}
                </Button>
              </form>

              <div className="mt-6 flex items-center gap-3">
                <div className="bg-border h-px flex-1" />
                <span className="text-muted-foreground text-xs">ou</span>
                <div className="bg-border h-px flex-1" />
              </div>

              <div className="mt-6 flex flex-col gap-2.5">
                <SocialButton
                  icon={<MicrosoftIcon className="size-4.5" />}
                  label="Continuer avec Microsoft"
                  disabled={pending}
                  onClick={() =>
                    void authClient.signIn.social({
                      provider: "microsoft",
                      callbackURL: `${window.location.origin}${returnTo ?? ""}`,
                      // Without this, better-auth sends OAuth failures to
                      // `${PUBLIC_SERVER_URL}/error?error=…` (bare API host,
                      // no route there) instead of back here — the
                      // validateSearch/useEffect toast above would never fire.
                      errorCallbackURL: `${window.location.origin}/sign-in`,
                    })
                  }
                />
                <SocialButton
                  icon={<GoogleIcon className="size-5" />}
                  label="Continuer avec Google"
                  disabled={pending}
                  onClick={() =>
                    void authClient.signIn.social({
                      provider: "google",
                      callbackURL: `${window.location.origin}${returnTo ?? ""}`,
                      errorCallbackURL: `${window.location.origin}/sign-in`,
                    })
                  }
                />
              </div>

              <p className="text-muted-foreground mt-4 text-center text-xs">
                Comptes professionnels Microsoft 365 et Google Workspace.
              </p>
            </>
          ) : (
            <form onSubmit={verifyOtp} className="mt-8 flex flex-col gap-2">
              <p className="text-muted-foreground text-sm">
                Saisissez le code à 6 chiffres envoyé à{" "}
                <span className="text-foreground font-medium">{e164}</span>.
              </p>
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
              <Button type="submit" className="mt-3" disabled={pending}>
                {pending ? "Vérification…" : "Se connecter"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="mt-1"
                disabled={pending}
                onClick={() => {
                  setStep("identifier");
                  setCode("");
                }}
              >
                Utiliser un autre numéro
              </Button>
            </form>
          )}

          <p className="text-muted-foreground mt-10 flex items-center justify-center gap-3 text-xs">
            <Link to="/confidentialite" className="hover:text-foreground">
              Confidentialité
            </Link>
            <span aria-hidden>·</span>
            <Link to="/conditions" className="hover:text-foreground">
              Conditions
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
